import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, 
  FolderOpen, 
  GraduationCap, 
  Users, 
  UserCheck, 
  Calendar, 
  FileText, 
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Building2,
  Landmark,
  BookOpen,
  ClipboardList,
  Briefcase,
  HelpCircle,
  Eye,
  TrendingUp,
  Shield,
  UserCog,
  ListChecks,
  FolderTree,
  Hash,
  MapPin,
  Tags,
  Award,
  Layers,
  Clock,
  Star,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { getAuthToken } from '../../lib/authToken';
import { APP_VERSION } from '../../lib/version';

const navigation = [
  { name: 'Tableau de bord', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'candidat', 'responsable_pedagogique', 'administratif'] },
  { name: 'Actions de formation', href: '/dossiers', icon: FolderOpen, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
  { name: 'Prestations', href: '/prestations', icon: Briefcase, roles: ['admin', 'administratif'] },
  { name: 'Formations', href: '/formations', icon: GraduationCap, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
  { name: 'Support peda', href: '/support-peda', icon: BookOpen, roles: ['admin', 'responsable_pedagogique', 'administratif', 'formateur'] },
  { name: 'Candidats', href: '/candidats', icon: Users, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
  { name: 'Sociétés', href: '/societes', icon: Building2, roles: ['admin'] },
  { name: 'Collaborateurs', href: '/formateurs', icon: UserCog, roles: ['admin', 'administratif'] },
  { name: 'Planning', href: '/planning', icon: Calendar, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
  { name: 'Facturation', href: '/facturation', icon: FileText, roles: ['admin'] },
  { name: 'Requetes', href: '/requetes', icon: ClipboardList, roles: ['admin', 'administratif'] },
  {
    name: 'Listes', icon: ListChecks, children: [
      { name: 'Domaines de formation', href: '/listes?tab=domaines', icon: FolderTree, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
      { name: 'Codes NSF', href: '/listes?tab=nsf', icon: Hash, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
      { name: 'Lieux de formation', href: '/listes?tab=lieux', icon: MapPin, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
      { name: 'Types de formation', href: '/listes?tab=types', icon: Tags, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
      { name: 'Certifications', href: '/listes?tab=certifications', icon: Award, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
      { name: 'Séquences', href: '/listes?tab=sequences', icon: Layers, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
      { name: 'Questionnaires de satisfaction', href: '/listes?tab=satisfaction', icon: Star, roles: ['admin', 'responsable_pedagogique', 'administratif'] },
    ],
  },
  {
    name: 'Portails', icon: Briefcase, children: [
      { name: 'Portail Formateur', href: '/portail-formateur', icon: Briefcase, roles: ['admin', 'formateur'] },
      { name: 'Portail Candidat', href: '/portail-candidat', icon: GraduationCap, roles: ['admin'] },
      { name: 'Portail Resp. Peda', href: '/portail-responsable', icon: Eye, roles: ['admin', 'administratif'] },
      { name: 'Portail Entreprise', href: '/portail-client', icon: Building2, roles: ['admin'] },
      { name: 'Portail Financeur', href: '/portail-financeur', icon: Landmark, roles: ['admin'] },
      { name: 'Portail Commercial', href: '/portail-commercial', icon: TrendingUp, roles: ['admin'] },
    ],
  },
  { name: 'Mon Planning', href: '/mon-planning', icon: Calendar, roles: ['formateur'] },
  { name: 'Mon activité', href: '/mon-activite', icon: Clock, roles: ['formateur', 'administratif', 'commercial'] },
  { name: 'Espace Commercial', href: '/commercial', icon: TrendingUp, roles: ['admin', 'commercial'] },
  { name: 'Utilisateurs', href: '/utilisateurs', icon: UserCheck, roles: ['admin'] },
];

export const DashboardLayout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [taskBadge, setTaskBadge] = useState(0);

  useEffect(() => {
    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'responsable_pedagogique' || user?.role === 'administratif';
    if (!isAdmin) return;
    const token = getAuthToken();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taches/count`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setTaskBadge(d.total || 0))
      .catch(() => {});
  }, [user, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavigation = navigation
    .map(item => item.children
      ? { ...item, children: item.children.filter(c => c.roles.some(role => hasRole(role)) && !(item.name === 'Portails' && user?.role === 'formateur' && c.href !== '/portail-formateur')) }
      : item)
    .filter(item => item.children ? item.children.length > 0 : item.roles.some(role => hasRole(role)));

  const getInitials = (nom, prenom) => {
    return `${prenom?.[0] || ''}${nom?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Compact with hover expand */}
      <aside 
        className={`
          fixed top-0 left-0 z-50 h-full bg-slate-900 text-slate-300 transform transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64'}
          lg:translate-x-0 ${sidebarExpanded ? 'lg:w-64' : 'lg:w-[72px]'}
        `}
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => setSidebarExpanded(false)}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
            <Link to="/dashboard" className="flex items-center gap-2 overflow-hidden">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <span className={`text-xl font-bold text-white font-['Plus_Jakarta_Sans'] whitespace-nowrap transition-opacity duration-200 ${sidebarExpanded || sidebarOpen ? 'opacity-100' : 'lg:opacity-0'}`}>
                OF-Manager
              </span>
            </Link>
            <button 
              className="lg:hidden text-slate-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto overflow-x-hidden">
            {filteredNavigation.map((item) => {
              if (item.children) {
                const childActive = item.children.some(c => location.pathname === c.href.split('?')[0] || location.pathname.startsWith(c.href.split('?')[0] + '/'));
                const isOpen = openGroups[item.name] ?? childActive;
                return (
                  <div key={item.name}>
                    <button
                      type="button"
                      data-testid={`nav-group-${item.name.toLowerCase()}`}
                      title={!sidebarExpanded && !sidebarOpen ? item.name : undefined}
                      className={`
                        w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all
                        ${childActive && !isOpen ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                      `}
                      onClick={() => {
                        setOpenGroups(p => ({ ...p, [item.name]: !isOpen }));
                        setSidebarExpanded(true);
                      }}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span className={`whitespace-nowrap transition-opacity duration-200 flex-1 text-left ${sidebarExpanded || sidebarOpen ? 'opacity-100' : 'lg:opacity-0 lg:w-0'}`}>
                        {item.name}
                      </span>
                      <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${sidebarExpanded || sidebarOpen ? '' : 'lg:hidden'}`} />
                    </button>
                    {isOpen && (sidebarExpanded || sidebarOpen) && (
                      <div className="mt-1 space-y-1 pl-5">
                        {item.children.map(child => {
                          const childPath = child.href.split('?')[0];
                          const isActive = child.href.includes('?')
                            ? location.pathname + location.search === child.href
                            : location.pathname === childPath || location.pathname.startsWith(childPath + '/');
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              data-testid={`nav-${child.href.slice(1)}`}
                              className={`
                                flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all
                                ${isActive ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                              `}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <child.icon className="w-4 h-4 flex-shrink-0" />
                              <span className="whitespace-nowrap">{child.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.href.slice(1)}`}
                  title={!sidebarExpanded && !sidebarOpen ? item.name : undefined}
                  className={`
                    flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all relative
                    ${isActive 
                      ? 'bg-indigo-600 text-white' 
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className={`whitespace-nowrap transition-opacity duration-200 ${sidebarExpanded || sidebarOpen ? 'opacity-100' : 'lg:opacity-0 lg:w-0'}`}>
                    {item.name}
                  </span>
                  {item.href === '/dashboard' && taskBadge > 0 && (
                    <span className={`ml-auto flex-shrink-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold bg-red-500 text-white ${sidebarExpanded || sidebarOpen ? '' : 'lg:absolute lg:top-1 lg:right-1'}`} data-testid="task-badge">
                      {taskBadge > 99 ? '99+' : taskBadge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-3 border-t border-slate-800">
            <div className={`flex items-center gap-3 px-3 py-3 rounded-lg bg-slate-800/50 ${sidebarExpanded || sidebarOpen ? '' : 'lg:justify-center'}`}>
              <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                {getInitials(user?.nom, user?.prenom)}
              </div>
              <div className={`flex-1 min-w-0 transition-opacity duration-200 ${sidebarExpanded || sidebarOpen ? 'opacity-100' : 'lg:opacity-0 lg:hidden'}`}>
                <p className="text-sm font-medium text-white truncate">
                  {user?.prenom} {user?.nom}
                </p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
            </div>
            <p
              className={`mt-2 text-[10px] text-slate-500 text-center font-mono ${sidebarExpanded || sidebarOpen ? '' : 'lg:hidden'}`}
              title="Version de l'application déployée"
              data-testid="app-version"
            >
              v{APP_VERSION}
            </p>
          </div>
        </div>
      </aside>

      {/* Main content - Adjusts based on sidebar state */}
      <div className={`transition-all duration-300 min-w-0 overflow-x-hidden ${sidebarExpanded ? 'lg:pl-64' : 'lg:pl-[72px]'}`}>
        {/* Top navbar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              className="lg:hidden text-slate-600 hover:text-slate-900"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {getInitials(user?.nom, user?.prenom)}
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.prenom} {user?.nom}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                  <Settings className="w-4 h-4 mr-2" />
                  Paramètres
                </DropdownMenuItem>
                {hasRole('admin') && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">
                      <Shield className="w-4 h-4 mr-2" />
                      Administration
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate('/mon-profil')} data-testid="profile-menu-item">
                  <Users className="w-4 h-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
